import React from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';

/**
 * DiscountInfoModal - Explains private company discount concept
 * Provides guidance on choosing appropriate discount percentage
 */
const DiscountInfoModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Private Company Discount</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center">
              <X size={24} />
            </button>
          </div>

          <div className="prose prose-blue max-w-none">
            <p className="text-gray-700 mb-4">
              <strong>Private Company Discount</strong> (also called "Marketability Discount" or "Illiquidity Discount") reflects the fact that private company shares are harder to sell than public company shares.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">Why Apply a Discount?</h3>
            <ul className="space-y-2 mb-6">
              <li className="flex gap-3">
                <span className="text-purple-600 font-bold">•</span>
                <div>
                  <strong>Limited Liquidity:</strong> Private shares can't be quickly sold on a public exchange
                </div>
              </li>
              <li className="flex gap-3">
                <span className="text-purple-600 font-bold">•</span>
                <div>
                  <strong>Smaller Buyer Pool:</strong> Fewer potential buyers compared to public stocks
                </div>
              </li>
              <li className="flex gap-3">
                <span className="text-purple-600 font-bold">•</span>
                <div>
                  <strong>Information Asymmetry:</strong> Less financial transparency than public companies
                </div>
              </li>
              <li className="flex gap-3">
                <span className="text-purple-600 font-bold">•</span>
                <div>
                  <strong>Control Factors:</strong> Minority stakes may have limited influence
                </div>
              </li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">Typical Discount Ranges</h3>
            <div className="space-y-3 mb-6">
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="font-semibold text-green-900">0-15% Discount</p>
                <p className="text-sm text-green-700">Strong growth, clear exit path, strategic buyers interested</p>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="font-semibold text-blue-900">20-30% Discount (Standard)</p>
                <p className="text-sm text-blue-700">Typical private company, stable operations, normal liquidity constraints</p>
              </div>

              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="font-semibold text-orange-900">35-50% Discount</p>
                <p className="text-sm text-orange-700">Limited growth, owner-dependent, difficult to sell, minority stake</p>
              </div>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">How to Choose Your Discount</h3>
            <p className="text-gray-700 mb-4">Consider these factors:</p>
            <ul className="space-y-2 mb-6 list-disc list-inside">
              <li className="text-gray-700"><strong>Financial Performance:</strong> Stronger = lower discount</li>
              <li className="text-gray-700"><strong>Management Team:</strong> Less owner-dependent = lower discount</li>
              <li className="text-gray-700"><strong>Market Position:</strong> Strategic value = lower discount</li>
              <li className="text-gray-700"><strong>Size:</strong> Larger companies often get lower discounts</li>
              <li className="text-gray-700"><strong>Exit Readiness:</strong> More prepared = lower discount</li>
            </ul>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-6">
              <p className="text-sm text-purple-900">
                <strong>💡 Recommendation:</strong> The standard 25% discount is a safe starting point. Adjust based on your business's specific strengths and weaknesses. A well-prepared business with strong growth can justify a 15-20% discount, while a struggling or owner-dependent business might warrant 30-40%.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="mt-6 w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors min-h-[44px]"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

DiscountInfoModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default DiscountInfoModal;
